import React, { useContext, useState } from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';
import { UserContext } from '../../context/UserContext';
import { useSearchParams } from 'react-router-dom';
import { EnterpriseClassification, Group, Sector } from '@esg/esg-global-types';
import AccessDenied from '../shared/AccessDenied';
import LoadingScreen from '../../components/shared/loading/LoadingScreen';
import { ReactComponent as GroupIcon } from '../../assets/icons/Group.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/Edit Outlined.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/Delete Outlined.svg';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { group_label } from '../../lib/app/group';
import PanelCompanies from '../../components/group_management/companies/PanelCompanies';
import { Drawer, Theme, useTheme } from '@mui/material';
import ConfigEditWidget, { EditInput } from '../../components/configuration/ConfigEditWidget';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import SectorSelect from '../../components/shared/input/select/SectorSelect';
import EnterpriseClassificationSelect from '../../components/shared/input/select/EnterpriseClassificationSelect';
import GuidanceSelect from '../../components/shared/input/select/GuidanceSelect';
import { CompanyContext } from '../../context/CompanyContext';
import { useModuleDurationTracking } from '../../hooks/useModuleDurationTracking';
import { getGroups, softDeleteGroup, updateGroup } from '../../graphql/services/group.service';
import { UpdateGroupClientData } from '@esg/syntiro-api-core';
import DeleteConfirmModal from '../../components/shared/modal/DeleteConfirmModal';
import { FEEDBACK_ACTION_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';

const SOURCE = 'GroupView.tsx';

/**
 * View for displaying the details and companies of a chosen Group
 * @param {void} addGroupCompany Function to add a newly created company in memory to be displayed within the app
 * @param {void} removeGroup Function to remove a newly deleted group's companies in memory
 * @returns {JSX.Element}
 */
const GroupView = () => {
  // Track module duration.
  useModuleDurationTracking('Configuration', 'Company Management');

  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [group, setGroup] = useState<Group | null>();
  const [displayWidgetPanelEdit, setDisplayWidgetPanelEdit] = useState<boolean>(false);
  const [openDialogDelete, setOpenDialogDelete] = useState<boolean>(false);
  const { addGroupCompany } = useContext(CompanyContext);
  const { removeGroupCompany } = useContext(CompanyContext);
  const { removeGroup } = useContext(CompanyContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  const sectorsInputFactory = (
    handleInputChange: (input_id: string, value: Array<Sector> | Sector | null) => void
  ) => {
    return (
      <SectorSelect
        master_list
        input_label="Sectors"
        multi_select
        tag_limit={4}
        select_limit={4}
        selected_options={group?.sectors as Array<Sector> | undefined}
        handleChangeSectors={(sectors: Array<Sector> | Sector | null) => {
          handleInputChange('sectors', sectors);
        }}
      />
    );
  };

  const enterpriseClassificationInputFactory = (
    handleInputChange: (input_id: string, value: EnterpriseClassification | null) => void
  ) => {
    return (
      <EnterpriseClassificationSelect
        clearable={false}
        selected_enterprise_classification={
          group?.enterprise_classification as EnterpriseClassification
        }
        handleChangeEnterpriseClassification={(
          enterprise_classification: EnterpriseClassification | null
        ) => {
          handleInputChange('enterprise_classification', enterprise_classification);
        }}
      />
    );
  };

  const guidanceInputFactory = (handleInputChange: (input_id: string, value: boolean) => void) => {
    return (
      <GuidanceSelect
        group_guidance_value={group?.guided_readiness}
        handleGuidanceTypeChange={(value: boolean | null) => {
          handleInputChange('guided_readiness', !!value);
        }}
      />
    );
  };

  const edit_inputs: Array<EditInput> = [
    {
      id: 'name',
      type: 'text',
      label: 'Name',
      defaultValue: group?.name
    },
    {
      id: 'sectors',
      type: 'node',
      label: 'Sectors',
      inputNodeFactory: sectorsInputFactory
    },
    {
      id: 'enterprise_classification',
      type: 'node',
      label: 'Enterprise Classification',
      inputNodeFactory: enterpriseClassificationInputFactory
    },
    {
      id: 'guided_readiness',
      type: 'node',
      label: 'Guided Readiness',
      inputNodeFactory: guidanceInputFactory
    }
  ];

  const handleEditClick = (): void => {
    setDisplayWidgetPanelEdit(true);
  };

  const handleDeleteClick = (): void => {
    setOpenDialogDelete(true);
  };

  const handleUpdateGroup = async (updated_group: Group): Promise<void> => {
    try {
      // Update row in database.
      const update_group_client_data: UpdateGroupClientData = {
        id: updated_group.id,
        name: updated_group.name,
        sectors: updated_group.sectors,
        enterprise_classification: updated_group.enterprise_classification,
        guided_readiness: updated_group.guided_readiness
      };
      await updateGroup({}, update_group_client_data);
      // Update row in memory.
      setGroup(updated_group);
      setDisplayWidgetPanelEdit(false);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: GroupView failed on an unknown error while calling handleUpdateGroup.',
          {
            updated_group: updated_group
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update Group. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  React.useEffect(() => {
    let group_found = false;
    if (location.state) {
      setGroup(location.state);
      setSearchParams({ group: location.state.id });
    } else {
      (async () => {
        const groups: Array<Group> = await getGroups();
        if (groups.length === 0) {
          navigate('/settings/group-management');
        }
        for (let i = 0; i < groups.length; i++) {
          if (groups[i].id === searchParams.get('group')) {
            setGroup(groups[i]);
            group_found = true;
          }
        }
        if (!group_found) {
          navigate('/settings/group-management');
        }
      })();
    }
  }, []);

  const handleButtonClickBack = () => {
    navigate('/settings/group-management');
  };

  const handleCloseDeleteModal = (): void => {
    setOpenDialogDelete(false);
  };

  const deleteRow = async (group: Group) => {
    try {
      await softDeleteGroup({}, { id: group.id });
      removeGroup(group);
      navigate('/settings/group-management');
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow'),
          {
            group_id: group?.id
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to delete group', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  return user === null || !group ? (
    <LoadingScreen />
  ) : user.super_admin ? (
    <>
      {/* Side Widget Panel */}
      <Drawer
        anchor={'right'}
        open={displayWidgetPanelEdit}
        onClose={() => setDisplayWidgetPanelEdit(false)}
        PaperProps={{ style: { width: '30%', padding: '1.5rem' } }}
      >
        <ConfigEditWidget
          widget_label={group_label.one}
          icon={<EditIcon width={40} fill={primary_blue} />}
          handleClose={() => setDisplayWidgetPanelEdit(false)}
          edit_entity={group}
          inputs={edit_inputs}
          handleEditInput={(input_id: string, value: string) =>
            group && setGroup({ ...group, [input_id]: value })
          }
          confirmEditFunction={(updated_group: Group) => handleUpdateGroup(updated_group)}
        />
      </Drawer>

      <DeleteConfirmModal
        open={openDialogDelete}
        allow_delete={true}
        delete_warning="This group will no longer be accessible through the platform."
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={group && group.name}
        handleDelete={group && (() => deleteRow(group))}
        allow_archive={false}
      />

      <Grid container spacing={2}>
        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              color: 'grey',
              mb: '0.5rem'
            }}
          >
            <IconButton
              size="large"
              aria-label="close"
              color="primary"
              onClick={handleButtonClickBack}
            >
              <Tooltip title="Back to groups">
                <ArrowBackIosNewIcon fontSize="large" />
              </Tooltip>
            </IconButton>
            <Box sx={{ mx: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon height={45} stroke="currentColor" />
                <Typography variant="h4" color="inherit" align="left">
                  {group.name}
                </Typography>
              </Box>

              <Typography
                variant="subtitle2"
                color="grey"
                align="left"
                component="div"
                sx={{ my: 1 }}
              >
                <b>Last Modified:</b> {moment().format('dddd, MMMM Do YYYY, hh:mm:ss')}
              </Typography>
            </Box>
            {user && user.super_admin && (
              <IconButton onClick={handleEditClick}>
                <EditIcon width={40} fill="grey" stroke="grey" strokeWidth={2} />
              </IconButton>
            )}
            {user && user.super_admin && (
              <IconButton onClick={handleDeleteClick}>
                <DeleteIcon width={40} fill="grey" stroke="grey" strokeWidth={2} />
              </IconButton>
            )}
          </Box>
          <Box sx={{ px: 8 }}>
            <Typography sx={{ textAlign: 'left', fontSize: '1.3rem', my: 6 }}>Companies</Typography>
            <PanelCompanies
              group={group}
              addGroupCompany={addGroupCompany}
              removeGroupCompany={removeGroupCompany}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  ) : (
    <AccessDenied />
  );
};

export default GroupView;
